import React, { useRef, useEffect, useState } from "react";
import { NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import "./Header.css";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../../assets/images/eco-logo.png";
import { Container, Row } from "react-bootstrap";
import { useSelector } from "react-redux";
import useAuth from "../../custom-hooks/useAuth";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase.config";
import { toast } from "react-toastify";
import QuickSearchModal from "../Ui/QuickSearchModal";
import ConfirmModal from "../Ui/ConfirmModal";
import AnnouncementBar from "./AnnouncementBar";

// 5 Focused links on desktop for calm spacing
const desktopNavLinks = [
  { path: "/shop", display: "Collection" },
  { path: "/about", display: "The Atelier" },
  { path: "/lookbook", display: "Lookbook" },
  { path: "/services", display: "Services" },
  { path: "/contact", display: "Showrooms" },
];

// Full navigation inside mobile drawer
const mobileNavLinks = [
  { path: "/", display: "Home", icon: "ri-home-4-line" },
  { path: "/shop", display: "Collection", icon: "ri-store-2-line" },
  { path: "/about", display: "The Atelier & Story", icon: "ri-compass-3-line" },
  { path: "/lookbook", display: "Editorial Lookbook", icon: "ri-layout-masonry-line" },
  { path: "/services", display: "Bespoke Services", icon: "ri-pencil-ruler-2-line" },
  { path: "/contact", display: "Flagship Showrooms", icon: "ri-map-pin-2-line" },
];

const Header = () => {
  const headerRef = useRef(null);
  const profileActionRef = useRef(null);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const [avatarError, setAvatarError] = useState(false);

  const totalQuantity = useSelector((state) => state.cart.totalQuantity);
  const wishlistQuantity = useSelector((state) => state.wishlist.totalQuantity);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();

  // Close menus when route changes
  useEffect(() => {
    setIsMobileDrawerOpen(false);
    setIsProfileOpen(false);
  }, [location.pathname]);

  // Reset avatar error state whenever currentUser photoURL updates
  useEffect(() => {
    setAvatarError(false);
  }, [currentUser?.photoURL]);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (isMobileDrawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileDrawerOpen]);

  useEffect(() => {
    let ticking = false;
    let isSticky = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const shouldBeSticky = window.scrollY > 30;
          if (shouldBeSticky !== isSticky) {
            isSticky = shouldBeSticky;
            if (shouldBeSticky) {
              headerRef.current?.classList.add("sticky__header");
            } else {
              headerRef.current?.classList.remove("sticky__header");
            }
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    const handleClickOutside = (event) => {
      if (
        profileActionRef.current &&
        !profileActionRef.current.contains(event.target)
      ) {
        setIsProfileOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleConfirmLogout = async () => {
    setIsLogoutModalOpen(false);
    setIsProfileOpen(false);
    setIsMobileDrawerOpen(false);
    try {
      await signOut(auth);
      toast.success("Successfully logged out");
      navigate("/login");
    } catch (err) {
      toast.error(err.message || "Failed to log out");
    }
  };

  const toggleProfileActions = () => {
    setIsProfileOpen((prev) => !prev);
  };

  const displayName = currentUser?.displayName || currentUser?.email?.split("@")[0] || "Member";
  const userInitial = displayName.charAt(0).toUpperCase();
  // UI only: shows the admin link. Access is enforced by AdminRoute + firestore.rules.
  const isAdmin = currentUser?.role === 'admin';

  return (
    <>
      {/* Dynamic Animated Announcement Bar */}
      <AnnouncementBar />

      {/* Main Navigation Bar */}
      <header className="header" ref={headerRef}>
        <Container>
          <Row>
            <div className="nav__wrapper">
              {/* Brand Logo */}
              <NavLink to={"/"} className="logo">
                <img src={logo} alt="L'Habilleur Logo" width="28" height="28" />
                <div className="logo__text">
                  <span className="logo__brand-title">L'HABILLEUR</span>
                  <span>ATELIER</span>
                </div>
              </NavLink>

              {/* Desktop Navigation Links (Hidden on <= 991px) */}
              <nav className="navigation d-none d-lg-flex" aria-label="Desktop menu">
                <ul className="menu">
                  {desktopNavLinks.map((item, index) => (
                    <li className="nav__item" key={index}>
                      <NavLink
                        to={item.path}
                        className={({ isActive }) =>
                          isActive ? "active-link" : ""
                        }
                      >
                        {item.display}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </nav>

              {/* Header Right Icons & Actions */}
              <div className="nav__icons">
                {/* Instant Search Button */}
                <button
                  type="button"
                  className="header__icon-btn"
                  onClick={() => setIsSearchOpen(true)}
                  title="Search Atelier Collection"
                  aria-label="Search"
                >
                  <i className="ri-search-line"></i>
                </button>

                {/* Wishlist Icon */}
                <button
                  type="button"
                  className="header__icon-btn"
                  onClick={() => navigate("/wishlist")}
                  title="Saved Pieces"
                  aria-label="Wishlist"
                >
                  <i className="ri-heart-3-line"></i>
                  {wishlistQuantity > 0 && (
                    <span className="header__icon-badge">{wishlistQuantity}</span>
                  )}
                </button>

                {/* Shopping Bag Icon */}
                <button
                  type="button"
                  className="header__icon-btn"
                  onClick={() => navigate("/cart")}
                  title="Shopping Bag"
                  aria-label="Shopping Cart"
                >
                  <i className="ri-shopping-bag-line"></i>
                  {totalQuantity > 0 && (
                    <span className="header__icon-badge">{totalQuantity}</span>
                  )}
                </button>

                {/* User Profile / Auth Links (Desktop Only - on mobile it is unified inside the single mobile menu) */}
                {currentUser ? (
                  <div className="profile d-none d-lg-block" ref={profileActionRef}>
                    <button
                      type="button"
                      className="profile__trigger-btn"
                      onClick={toggleProfileActions}
                      aria-label="Toggle user profile menu"
                    >
                      {currentUser.photoURL && !avatarError ? (
                        <img
                          src={currentUser.photoURL}
                          alt={displayName}
                          className="profile-picture"
                          referrerPolicy="no-referrer"
                          crossOrigin="anonymous"
                          onError={() => setAvatarError(true)}
                        />
                      ) : (
                        <span className="profile-picture-initial">
                          {userInitial}
                        </span>
                      )}
                      <span className="profile__trigger-name d-none d-xl-inline">
                        {displayName.split(" ")[0]}
                      </span>
                      <i className={`ri-arrow-down-s-line profile__chevron ${isProfileOpen ? "rotated" : ""}`}></i>
                    </button>

                    <AnimatePresence>
                      {isProfileOpen && (
                        <motion.div
                          className="profile__dropdown"
                          initial={{ opacity: 0, y: 10, scale: 0.96 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: 8, scale: 0.96 }}
                          transition={{ duration: 0.18 }}
                        >
                          <div className="profile__dropdown-header">
                            <div className="profile__dropdown-avatar">
                              {currentUser.photoURL && !avatarError ? (
                                <img
                                  src={currentUser.photoURL}
                                  alt={displayName}
                                  referrerPolicy="no-referrer"
                                  crossOrigin="anonymous"
                                  onError={() => setAvatarError(true)}
                                />
                              ) : (
                                <span>{userInitial}</span>
                              )}
                            </div>
                            <div className="profile__dropdown-meta">
                              <h6 className="profile__dropdown-name">{displayName}</h6>
                              <p className="profile__dropdown-email">{currentUser.email}</p>
                              <span className="profile__dropdown-badge">
                                <i className="ri-shield-check-fill"></i> Verified Member
                              </span>
                            </div>
                          </div>

                          <div className="profile__dropdown-divider"></div>

                          <ul className="profile__dropdown-list">
                            {isAdmin && (
                              <li>
                                <Link
                                  to="/admin"
                                  className="profile__dropdown-link"
                                  style={{
                                    background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.18), rgba(212, 175, 55, 0.05))',
                                    border: '1px solid rgba(212, 175, 55, 0.4)',
                                    borderRadius: '10px',
                                    marginBottom: '8px',
                                  }}
                                  onClick={() => setIsProfileOpen(false)}
                                >
                                  <div
                                    className="profile__dropdown-icon"
                                    style={{
                                      background: '#d4af37',
                                      color: '#070e1b',
                                      fontWeight: 'bold',
                                    }}
                                  >
                                    <i className="ri-shield-flash-line"></i>
                                  </div>
                                  <div className="profile__dropdown-text">
                                    <span style={{ color: '#d4af37', fontWeight: '800' }}>Command Center</span>
                                    <small style={{ color: '#c59b27', fontWeight: '600' }}>Atelier Admin Mode</small>
                                  </div>
                                </Link>
                              </li>
                            )}

                            <li>
                              <Link
                                to="/profile"
                                className="profile__dropdown-link"
                                onClick={() => setIsProfileOpen(false)}
                              >
                                <div className="profile__dropdown-icon">
                                  <i className="ri-user-settings-line"></i>
                                </div>
                                <div className="profile__dropdown-text">
                                  <span>My Profile</span>
                                  <small>Account, address & security</small>
                                </div>
                              </Link>
                            </li>

                            <li>
                              <Link
                                to="/orders"
                                className="profile__dropdown-link"
                                onClick={() => setIsProfileOpen(false)}
                              >
                                <div className="profile__dropdown-icon">
                                  <i className="ri-shopping-bag-3-line"></i>
                                </div>
                                <div className="profile__dropdown-text">
                                  <span>My Orders</span>
                                  <small>Track active shipments</small>
                                </div>
                              </Link>
                            </li>

                            <li>
                              <Link
                                to="/wishlist"
                                className="profile__dropdown-link"
                                onClick={() => setIsProfileOpen(false)}
                              >
                                <div className="profile__dropdown-icon">
                                  <i className="ri-heart-3-line"></i>
                                </div>
                                <div className="profile__dropdown-text">
                                  <span>Saved Pieces</span>
                                  <small>{wishlistQuantity} item{wishlistQuantity === 1 ? '' : 's'} saved</small>
                                </div>
                              </Link>
                            </li>

                            <li>
                              <Link
                                to="/cart"
                                className="profile__dropdown-link"
                                onClick={() => setIsProfileOpen(false)}
                              >
                                <div className="profile__dropdown-icon">
                                  <i className="ri-shopping-cart-2-line"></i>
                                </div>
                                <div className="profile__dropdown-text">
                                  <span>Shopping Bag</span>
                                  <small>{totalQuantity} item{totalQuantity === 1 ? '' : 's'} added</small>
                                </div>
                              </Link>
                            </li>

                            <li>
                              <Link
                                to="/shop"
                                className="profile__dropdown-link"
                                onClick={() => setIsProfileOpen(false)}
                              >
                                <div className="profile__dropdown-icon">
                                  <i className="ri-store-2-line"></i>
                                </div>
                                <div className="profile__dropdown-text">
                                  <span>Browse Atelier</span>
                                  <small>Explore full catalogue</small>
                                </div>
                              </Link>
                            </li>
                          </ul>

                          <div className="profile__dropdown-divider"></div>

                          <button
                            type="button"
                            className="profile__dropdown-logout"
                            onClick={() => {
                              setIsProfileOpen(false);
                              setIsLogoutModalOpen(true);
                            }}
                          >
                            <i className="ri-logout-box-r-line"></i>
                            <span>Sign Out</span>
                          </button>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  /* Desktop Auth Links */
                  <div className="auth-links d-none d-lg-flex">
                    <NavLink
                      to="/login"
                      className={({ isActive }) =>
                        isActive ? "auth-link-login active" : "auth-link-login"
                      }
                    >
                      Sign In
                    </NavLink>
                    <NavLink
                      to="/signup"
                      className={({ isActive }) =>
                        isActive ? "auth-link-signup active" : "auth-link-signup"
                      }
                    >
                      Register
                    </NavLink>
                  </div>
                )}

                {/* Mobile / Tablet Hamburger Button */}
                <button
                  type="button"
                  className="header__icon-btn mobile__menu-btn d-lg-none"
                  onClick={() => setIsMobileDrawerOpen(true)}
                  aria-label="Open mobile menu"
                  title="Menu"
                >
                  <i className="ri-menu-3-line"></i>
                </button>
              </div>
            </div>
          </Row>
        </Container>
      </header>

      {/* Luxury Slide-Out Navigation Drawer for Mobile & Tablet */}
      <AnimatePresence>
        {isMobileDrawerOpen && (
          <div
            className="mobile-drawer__backdrop"
            onClick={() => setIsMobileDrawerOpen(false)}
          >
            <motion.div
              className="mobile-drawer"
              onClick={(e) => e.stopPropagation()}
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.28, ease: "easeOut" }}
            >
              {/* Drawer Top Bar */}
              <div className="mobile-drawer__header">
                <div className="logo">
                  <img src={logo} alt="L'Habilleur Logo" width="28" height="28" />
                  <div className="logo__text">
                    <span className="logo__brand-title">L'HABILLEUR</span>
                    <span>ATELIER</span>
                  </div>
                </div>
                <button
                  type="button"
                  className="mobile-drawer__close"
                  onClick={() => setIsMobileDrawerOpen(false)}
                  aria-label="Close navigation"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>

              {/* Drawer Main Navigation Links */}
              <div className="mobile-drawer__body">
                <span className="mobile-drawer__section-label">Navigation</span>
                <ul className="mobile-drawer__links">
                  {mobileNavLinks.map((item, idx) => (
                    <li key={idx}>
                      <NavLink
                        to={item.path}
                        end={item.path === "/"}
                        className={({ isActive }) =>
                          isActive ? "mobile-drawer__link active" : "mobile-drawer__link"
                        }
                        onClick={() => setIsMobileDrawerOpen(false)}
                      >
                        <i className={item.icon}></i>
                        <span>{item.display}</span>
                      </NavLink>
                    </li>
                  ))}
                </ul>

                <div className="mobile-drawer__divider"></div>

                {/* Quick Utility Links */}
                <span className="mobile-drawer__section-label">Quick Access</span>
                <div className="mobile-drawer__quick">
                  <Link
                    to="/wishlist"
                    className="mobile-drawer__quick-item"
                    onClick={() => setIsMobileDrawerOpen(false)}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <i className="ri-heart-3-line"></i>
                      <span>Saved Pieces</span>
                    </div>
                    {wishlistQuantity > 0 && (
                      <span className="badge bg-danger rounded-pill">{wishlistQuantity}</span>
                    )}
                  </Link>

                  <Link
                    to="/cart"
                    className="mobile-drawer__quick-item"
                    onClick={() => setIsMobileDrawerOpen(false)}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <i className="ri-shopping-bag-line"></i>
                      <span>Shopping Bag</span>
                    </div>
                    {totalQuantity > 0 && (
                      <span className="badge bg-primary rounded-pill">{totalQuantity}</span>
                    )}
                  </Link>

                  <Link
                    to="/profile"
                    className="mobile-drawer__quick-item"
                    onClick={() => setIsMobileDrawerOpen(false)}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <i className="ri-user-settings-line"></i>
                      <span>My Profile</span>
                    </div>
                  </Link>

                  <Link
                    to="/orders"
                    className="mobile-drawer__quick-item"
                    onClick={() => setIsMobileDrawerOpen(false)}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <i className="ri-file-list-3-line"></i>
                      <span>My Orders</span>
                    </div>
                  </Link>

                  {isAdmin && (
                    <Link
                      to="/admin"
                      className="mobile-drawer__quick-item"
                      style={{
                        background: 'rgba(212, 175, 55, 0.15)',
                        border: '1px solid var(--admin-gold)',
                        color: 'var(--admin-gold)',
                        fontWeight: 'bold',
                      }}
                      onClick={() => setIsMobileDrawerOpen(false)}
                    >
                      <div className="d-flex align-items-center gap-2">
                        <i className="ri-shield-flash-line"></i>
                        <span>Atelier Command Center</span>
                      </div>
                    </Link>
                  )}
                </div>
              </div>

              {/* Drawer User Profile / Auth Area */}
              <div className="mobile-drawer__footer">
                {currentUser ? (
                  <div className="mobile-drawer__user">
                    <div className="d-flex align-items-center gap-2 mb-3">
                      {currentUser.photoURL && !avatarError ? (
                        <img
                          src={currentUser.photoURL}
                          alt={displayName}
                          className="profile-picture"
                          referrerPolicy="no-referrer"
                          crossOrigin="anonymous"
                          onError={() => setAvatarError(true)}
                        />
                      ) : (
                        <span className="profile-picture-initial">{userInitial}</span>
                      )}
                      <div>
                        <strong className="d-block text-dark small">{displayName}</strong>
                        <span className="text-muted" style={{ fontSize: "0.75rem" }}>
                          {currentUser.email}
                        </span>
                      </div>
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-danger btn-sm w-100"
                      onClick={() => {
                        setIsMobileDrawerOpen(false);
                        setIsLogoutModalOpen(true);
                      }}
                    >
                      <i className="ri-logout-box-r-line me-1"></i> Sign Out
                    </button>
                  </div>
                ) : (
                  <div className="mobile-drawer__auth d-flex gap-2">
                    <Link
                      to="/login"
                      className="btn btn-outline-dark btn-sm flex-fill"
                      onClick={() => setIsMobileDrawerOpen(false)}
                    >
                      Sign In
                    </Link>
                    <Link
                      to="/signup"
                      className="btn btn-primary btn-sm flex-fill"
                      onClick={() => setIsMobileDrawerOpen(false)}
                    >
                      Register
                    </Link>
                  </div>
                )}

                <div className="mobile-drawer__concierge mt-3 text-center">
                  <small className="text-muted d-block" style={{ fontSize: '0.75rem' }}>
                    Concierge & WhatsApp VIP Desk
                  </small>
                  <a
                    href="tel:+20221234567"
                    className="fw-bold text-dark text-decoration-none"
                    style={{ fontSize: '0.85rem' }}
                  >
                    +20 (2) 2123-4567
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Quick Search Modal */}
      <QuickSearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />

      {/* Sign Out Confirmation Modal */}
      <ConfirmModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleConfirmLogout}
        title="Sign Out Confirmation"
        badge="Atelier Security"
        message="Are you sure you want to sign out of your account? Your shopping bag and saved pieces will remain securely preserved in your cloud profile for your next visit."
        confirmText="Yes, Sign Out"
        cancelText="Stay Signed In"
        confirmVariant="danger"
        icon="ri-logout-box-r-line"
      />
    </>
  );
};

export default Header;
